// components/orders/MagnetsFrameModal.jsx
import React, { useState, useEffect } from 'react';
import { CloseIcon, EditPencil, DeleteICon } from '../../public/icons';
import reviewService from '../../services/reviewService';
import orderService from '../../services/ordersRouteServce';

const MagnetsFrameModal = ({
  isOpen,
  onClose,
  orderId,
  giftId,
  gift,
  orderNumber,
  onSaved
}) => {
  /* ── Saved magnets list ── */
  const [allMagnets, setAllMagnets] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);

  /* ── Current form ── */
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryStory, setMemoryStory] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);

  /* ── Upload state ── */
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  /* ── Save state ── */
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /* ── Pre-fill from the gift metadata on open ── */
  useEffect(() => {
    if (!isOpen) return;
    setSaveError('');
    setUploadError('');
    if (gift?.title) setMemoryTitle(gift.title);
  }, [isOpen, gift]);

  /* ── Load any previously saved content for this slot ──
     Prefers the structured `magnets[]` array; falls back to
     the flat memoryTitle/description/media for legacy records. */
  useEffect(() => {
    if (!isOpen || !orderId || !giftId) return;

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const res = await orderService.getFutureGiftContent(orderId, giftId);
        if (cancelled) return;
        const data = res?.data;
        if (!data) return;

        /* 🟢 Prefer structured magnets */
        if (Array.isArray(data.magnets) && data.magnets.length > 0) {
          setAllMagnets(
            data.magnets.map((m) => ({
              memoryTitle: m.memoryTitle || '',
              memoryStory: m.memoryStory || '',
              uploadedImages: Array.isArray(m.uploadedImages)
                ? m.uploadedImages
                : []
            }))
          );
          return;
        }

        /* Legacy fallback — reconstruct one magnet from flat fields */
        if (
          data.memoryTitle ||
          data.description ||
          (Array.isArray(data.media) && data.media.length > 0)
        ) {
          setAllMagnets([
            {
              memoryTitle: data.memoryTitle || '',
              memoryStory: data.description || '',
              uploadedImages: Array.isArray(data.media) ? data.media : []
            }
          ]);
        }
      } catch (err) {
        console.error('Failed to load saved magnet content:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId, giftId]);

  if (!isOpen) return null;

  /* ── Has current form content ── */
  const hasCurrentContent =
    memoryTitle.trim() !== '' ||
    memoryStory.trim() !== '' ||
    uploadedImages.length > 0;

  /* ── Upload to Cloudinary ── */
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploadError('');
    setIsUploading(true);

    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 10 * 1024 * 1024) {
          setUploadError(`${file.name} exceeds 10MB and was skipped.`);
          continue;
        }
        const result = await reviewService.uploadImageToCloudinary(file);
        uploaded.push({
          url: result.url,
          publicId: result.publicId,
          name: file.name,
          type: 'image'
        });
      }
      setUploadedImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploadError('Failed to upload one or more images. Please try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── Reset form ── */
  const resetForm = () => {
    setMemoryTitle('');
    setMemoryStory('');
    setUploadedImages([]);
    setUploadError('');
    setEditingIndex(null);
  };

  /* ── "+ Add another" ── */
  const handleAddAnother = () => {
    if (!hasCurrentContent) return;

    const magnet = {
      memoryTitle: memoryTitle.trim(),
      memoryStory: memoryStory.trim(),
      uploadedImages: [...uploadedImages]
    };

    setAllMagnets((prev) => {
      if (editingIndex !== null) {
        const next = [...prev];
        next[editingIndex] = magnet;
        return next;
      }
      return [...prev, magnet];
    });

    resetForm();
  };

  /* ── Edit saved magnet ── */
  const handleEditSavedMagnet = (index) => {
    const magnet = allMagnets[index];
    setMemoryTitle(magnet.memoryTitle);
    setMemoryStory(magnet.memoryStory);
    setUploadedImages(magnet.uploadedImages);
    setEditingIndex(index);

    document
      .getElementById('photo-upload-section')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  /* ── Delete saved magnet ── */
  const handleDeleteSavedMagnet = (index) => {
    setAllMagnets((prev) => prev.filter((_, i) => i !== index));

    if (editingIndex === index) {
      resetForm();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  /* ── Final save → server ──
     Sends the full structured magnets array. Nothing is lost. */
  const handleFinalSave = async () => {
    setSaveError('');

    /* If the user is still editing a draft that isn't committed,
       commit it in-memory first. */
    let finalMagnets = [...allMagnets];

    if (hasCurrentContent) {
      const magnet = {
        memoryTitle: memoryTitle.trim(),
        memoryStory: memoryStory.trim(),
        uploadedImages: [...uploadedImages]
      };

      if (editingIndex !== null) {
        finalMagnets[editingIndex] = magnet;
      } else {
        finalMagnets.push(magnet);
      }
    }

    if (finalMagnets.length === 0) {
      setSaveError('Add at least one magnet before saving.');
      return;
    }

    if (!orderId || !giftId) {
      setSaveError('Missing order or gift reference.');
      return;
    }

    setIsSaving(true);
    try {
      await orderService.saveFutureGiftContent(orderId, giftId, {
        /* 🟢 Structured payload — every magnet preserved */
        magnets: finalMagnets.map((m) => ({
          memoryTitle: m.memoryTitle,
          memoryStory: m.memoryStory,
          uploadedImages: m.uploadedImages
        })),
        revealDate: gift?.deliveryDate || null,
        revealTime: ''
      });

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Save magnets content failed:', err);
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not save. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Preview visible when there are committed magnets and not editing ── */
  const showPreview = allMagnets.length > 0 && editingIndex === null;

  const saveLabel = (() => {
    if (isSaving) return 'Saving…';
    if (allMagnets.length === 0) return 'Save future gift';
    return `Save ${allMagnets.length} future gift${
      allMagnets.length !== 1 ? 's' : ''
    }`;
  })();

  const saveDisabled =
    (allMagnets.length === 0 && !hasCurrentContent) || isUploading || isSaving;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={() => {
          if (!isSaving) onClose();
        }}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mx-6 mb-4 mt-6 pb-2 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                Personalise your Magnets
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                {allMagnets.length > 0
                  ? `You have ${allMagnets.length} magnet${
                      allMagnets.length !== 1 ? 's' : ''
                    } saved`
                  : 'Each magnet holds a photo on the front and a story behind it.'}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded disabled:opacity-50"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* ───────── Saved magnets ───────── */}
            {allMagnets.length > 0 && (
              <div className="mb-8">
                <div className="space-y-6">
                  {allMagnets.map((magnet, index) => (
                    <div key={index} className="flex gap-3 mb-6">
                      <div className="overflow-hidden flex-shrink-0">
                        {magnet.uploadedImages.length > 0 ? (
                          <img
                            src={magnet.uploadedImages[0].url}
                            alt={`Magnet ${index + 1}`}
                            className="h-32 w-32 object-cover"
                          />
                        ) : (
                          <img
                            src="https://www.realsimple.com/thmb/1nO0GmEuF87RSxFBmTWtfo6TZW0=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/the-best-gift-giver-GettyImages-1706575747-2089a300e6594496b7f558585a4baefb.jpg"
                            alt="Sample magnet"
                            className="h-32 w-32 object-cover"
                          />
                        )}
                      </div>
                      <div className="flex gap-4 flex-1 min-w-0">
                        <div className="text-left min-w-0 flex-1">
                          <h3 className="text-xl font-bold text-[#1C2A46] mb-0.5 break-words">
                            {magnet.memoryTitle || 'I Love you Mom'}
                          </h3>
                          <p className="text-[#6B635D] text-sm leading-relaxed line-clamp-4">
                            {magnet.memoryStory ||
                              'The Travel Memories collection features 6 carefully selected magnets, each with a beautiful image on the front and a meaningful story on the back. Perfect for decorating your fridge, office space, or as a thoughtful gift for someone special.'}
                          </p>
                        </div>

                        {/* Edit + Delete buttons */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0 self-start">
                          <button
                            onClick={() => handleEditSavedMagnet(index)}
                            className="text-secondary font-medium flex items-center gap-2 hover:text-[#C49660] transition-colors text-sm"
                            title="Edit this magnet"
                          >
                            <EditPencil />
                            Edit
                          </button>

                          <button
                            onClick={() => handleDeleteSavedMagnet(index)}
                            className="text-[#C03A2B] font-medium flex items-center gap-2 hover:text-[#A82121] transition-colors text-sm"
                            title="Delete this magnet"
                          >
                            <DeleteICon size={18} color="#C03A2B" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ───────── Editing banner ───────── */}
            {editingIndex !== null && (
              <div className="mb-5 bg-[#FFF4D6] border-l-4 border-[#D8A100] px-4 py-3 rounded-r">
                <p className="text-sm text-[#8F7F2F] text-left">
                  ✏️ Editing magnet {editingIndex + 1}. Make your changes and
                  click <strong>Save changes</strong> to update it.
                </p>
              </div>
            )}

            {/* ───────── Form ───────── */}
            <div
              className="flex flex-col sm:flex-row gap-5 w-full"
              id="photo-upload-section"
            >
              {/* Photo upload */}
              <div className="mb-6 w-full sm:w-1/2">
                <h4 className="text-base font-normal text-[#1C2A46] mb-3 text-left">
                  Add Photo
                </h4>
                <div
                  className={`bg-black h-72 w-52 flex flex-col justify-center items-center transition-opacity ${
                    isUploading
                      ? 'cursor-wait'
                      : 'cursor-pointer hover:opacity-90'
                  }`}
                  onClick={() => {
                    if (!isUploading) {
                      document.getElementById('magnets-photo-upload').click();
                    }
                  }}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" />
                      <p className="text-sm text-white mt-2">Uploading…</p>
                    </>
                  ) : uploadedImages.length > 0 ? (
                    <div className="relative w-full h-full">
                      <img
                        src={uploadedImages[0].url}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover"
                      />
                      <div
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer hover:bg-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(0);
                        }}
                      >
                        ×
                      </div>
                      {uploadedImages.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 text-xs rounded">
                          +{uploadedImages.length - 1} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 5v14M5 12h14"
                            stroke="#1C2A46"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-white mt-2 text-center px-2">
                        PNG, JPG, JPEG <br />
                        (max size. 10mb)
                      </p>
                    </>
                  )}
                </div>

                <input
                  id="magnets-photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  multiple
                  disabled={isUploading}
                />

                {uploadError && (
                  <p className="mt-2 text-xs text-red-600 text-left">
                    {uploadError}
                  </p>
                )}
              </div>

              {/* Title + Story */}
              <div className="w-full sm:w-1/2">
                <div className="mb-4">
                  <h4 className="text-base font-normal text-[#1C2A46] mb-3 text-left">
                    Memory Title (optional)
                  </h4>
                  <input
                    type="text"
                    placeholder='e.g. "For When You Need a Reminder"'
                    value={memoryTitle}
                    onChange={(e) => setMemoryTitle(e.target.value)}
                    className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light"
                  />
                </div>

                <div>
                  <h4 className="text-base font-normal text-[#1C2A46] mb-3 text-left">
                    Memory Story (Back of magnet)*
                  </h4>
                  <textarea
                    placeholder="Tell the story behind this moment. who was there? what happened? and why does it matter?"
                    value={memoryStory}
                    onChange={(e) => {
                      if (e.target.value.length <= 2000) {
                        setMemoryStory(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#666666] placeholder:font-light h-40"
                    maxLength="2000"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {memoryStory.length}/2000
                  </div>
                </div>
              </div>
            </div>

            {/* ───────── "+ Add another" / "Save changes" ───────── */}
            <div className="flex justify-end items-start mt-4">
              <button
                onClick={handleAddAnother}
                disabled={isUploading || !hasCurrentContent}
                className={`w-40 border py-3 font-medium transition-colors ${
                  isUploading || !hasCurrentContent
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-[#DBDBDB] text-[#1C2A46] hover:border-[#D2A146]'
                }`}
              >
                {editingIndex !== null ? 'Save changes' : '+ Add another'}
              </button>
            </div>

            {saveError && (
              <p className="text-sm text-red-600 mt-4 text-left">
                {saveError}
              </p>
            )}
          </div>

          {/* ───────── Final save ───────── */}
          <div className="p-6">
            <button
              onClick={handleFinalSave}
              disabled={saveDisabled}
              className={`w-full py-3 font-medium transition-colors ${
                saveDisabled
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#D2A146] text-white hover:bg-[#C49660]'
              }`}
            >
              {saveLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MagnetsFrameModal;